import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  Space,
  message,
  Tag,
  Tooltip,
  Popconfirm,
  Modal,
  Input,
  Checkbox as AntCheckbox,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ColumnHeightOutlined,
  UploadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { dispatchService, Dispatch } from '../services/dispatches';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useListingTableFocus } from '../hooks/useListingTableFocus';
import { useListingColumnsPersistence } from '../hooks/useListingColumnsPersistence';
import { useListingColumnWidthsPersistence } from '../hooks/useListingColumnWidthsPersistence';
import { useListingSortPersistence } from '../hooks/useListingSortPersistence';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';
import {
  Table as FlowbiteTable,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Alert,
} from 'flowbite-react';
import { Resizable } from 'react-resizable';

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};
type FilterType = 'all' | 'Plan' | 'Collect' | 'Return';
type SortOrder = 'ascend' | 'descend' | null;

interface FlowbiteResizableHeaderProps {
  width?: number | string;
  maxWidth?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
  onResizeStop?: (e: any, data: { size: { width: number } }) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const FlowbiteResizableHeader: React.FC<FlowbiteResizableHeaderProps> = ({
  width,
  maxWidth,
  onResize,
  onResizeStop,
  onClick,
  className,
  children,
}) => {
  if (typeof width !== 'number' || !onResize) {
    return (
      <TableHeadCell
        className={className}
        style={width ? { width } : undefined}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={(e, data) => {
        const newWidth = Math.max(data.size.width, 50);
        onResize(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      onResizeStop={(e, data) => {
        if (!onResizeStop) {
          return;
        }
        const newWidth = Math.max(data.size.width, 50);
        onResizeStop(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      resizeHandles={['e']}
      handle={
        <span
          className="resizable-table-handle"
          style={{ height: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      maxConstraints={typeof maxWidth === 'number' ? [maxWidth, 0] : undefined}
      minConstraints={[50, 0]}
    >
      <TableHeadCell
        className={className ? `relative ${className}` : 'relative'}
        style={{ width }}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    </Resizable>
  );
};

export const Dispatches: React.FC = () => {
  const listingKey = 'dispatches';
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [excludedRowKeys, setExcludedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [columnWidthByKey, setColumnWidthByKey] = useState<Record<string, number>>({});
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [columnsSearch, setColumnsSearch] = useState('');
  const [pendingVisibleKeys, setPendingVisibleKeys] = useState<React.Key[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const perPage = 200;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [statusCountsFromApi, setStatusCountsFromApi] = useState<{ all: number; Plan: number; Collect: number; Return: number } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    const stateNotice = (location.state as { notice?: { type: 'success' | 'failure'; message: string } } | null)?.notice;
    if (stateNotice) {
      setSubmitNotice(stateNotice);
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location, navigate]);
  const { user } = useAuth();
  const canView = hasPermission(user, 'projects.dispatch.view');
  const canCreate = hasPermission(user, 'projects.dispatch.create');
  const canDelete = hasPermission(user, 'projects.dispatch.delete');


  useEffect(() => {
    if (!canView) {
      setDispatches([]);
      return;
    }
    loadDispatches(1, 'reset');
  }, [canView]);


  const loadDispatches = useCallback(async (pageToLoad: number, mode: 'reset' | 'append') => {
    if (!canView) {
      return;
    }
    if (mode === 'reset') {
      setLoading(true);
      setPage(1);
      setHasMore(true);
      loadingMoreRef.current = false;
    } else {
      if (loadingMoreRef.current) {
        return;
      }
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }
    try {
      const response = await dispatchService.getAll({ page: pageToLoad, per_page: perPage });
      const { items, total, statusCounts: apiStatusCounts, currentPage, lastPage } = normalizeDispatchesResponse(response);
      setDispatches((prev) => (mode === 'append' ? [...prev, ...items] : items));
      const resolvedTotal = typeof total === 'number' && total > 0 ? total : items.length;
      setTotalCount(resolvedTotal);
      setStatusCountsFromApi(apiStatusCounts);
      setPage(currentPage || pageToLoad);
      if (lastPage && currentPage) {
        setHasMore(currentPage < lastPage);
      } else {
        setHasMore(items.length >= perPage);
      }
    } catch {
      message.error('Failed to load dispatches');
    } finally {
      if (mode === 'reset') {
        setLoading(false);
      } else {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [canView, perPage]);

  const normalizeDispatchesResponse = (response: any) => {
    const raw = response?.data ?? response;
    const meta = raw?.meta;
    const payload = raw?.data && !Array.isArray(raw?.data) ? raw.data : raw;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    const total = Number(payload?.total ?? meta?.total ?? 0);
    const currentPage = Number(payload?.current_page ?? 0);
    const lastPage = Number(payload?.last_page ?? 0);
    const nextPageExists = false;
    const statusCountsMeta = meta?.status_counts;
    const statusCounts = statusCountsMeta
      ? {
          all: total || 0,
          Plan: Number(statusCountsMeta.Plan ?? statusCountsMeta.plan ?? 0),
          Collect: Number(statusCountsMeta.Collect ?? statusCountsMeta.collect ?? 0),
          Return: Number(statusCountsMeta.Return ?? statusCountsMeta.return ?? 0),
        }
      : null;
    return { items, nextPageExists, total, statusCounts, currentPage, lastPage };
  };

  const handleImportNavigate = () => {
    navigate('/dispatches/import');
  };

  const handleBatchExport = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    try {
      const filters = filter === 'all' ? {} : { status: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      const blob = await dispatchService.export(payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dispatch-export-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to export dispatches');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await dispatchService.delete(id);
      message.success('Dispatch deleted successfully');
      loadDispatches(1, 'reset');
    } catch {
      message.error('Failed to delete dispatch');
    }
  };

  const handleBatchDelete = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    setDeleting(true);
    try {
      const filters = filter === 'all' ? {} : { status: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      await dispatchService.batchDelete(payload);
      message.success('Dispatches deleted successfully');
      setSelectedRowKeys([]);
      setSelectAll(false);
      setExcludedRowKeys([]);
      loadDispatches(1, 'reset');
    } catch {
      message.error('Failed to delete dispatches');
    } finally {
      setDeleting(false);
    }
  };

  const formatDispatchNumber = (dispatchId: number, referenceNumber?: string) => {
    return referenceNumber || `#${dispatchId.toString().padStart(7, '0')}`;
  };

  const formatTaskLabel = (record: Dispatch) => {
    const taskTemplateName =
      record.task?.taskTemplate?.name
      || record.task?.task_template?.name;
    return taskTemplateName || '-';
  };

  const formatDateValue = (date?: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '-');
  const formatYearMonthValue = (value?: string | number | null) => {
    if (value == null) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (/^(0[1-9]|1[0-2])\/\d{4}$/.test(raw)) return raw;
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return `${ymd[2]}/${ymd[1]}`;
    if (/^\d{4}$/.test(raw)) return `01/${raw}`;
    return raw;
  };
  const yearMonthSortKey = (value?: string | number | null) => {
    const formatted = formatYearMonthValue(value);
    const match = formatted.match(/^(0[1-9]|1[0-2])\/(\d{4})$/);
    if (!match) return 0;
    return Number(match[2]) * 100 + Number(match[1]);
  };
  const compareYearMonth = (a?: string | number | null, b?: string | number | null) =>
    yearMonthSortKey(a) - yearMonthSortKey(b);

  const getChecklistLabel = (items?: Array<{ completed?: boolean }>) => {
    const total = items?.length || 0;
    if (!total) return '-';
    const completed = items?.filter((item) => item.completed).length || 0;
    return `${completed}/${total}`;
  };

  const getPhotoCountLabel = (items?: Array<{ attachment?: string | null }>) => {
    const total = items?.filter((item) => item.attachment).length || 0;
    return total ? String(total) : '-';
  };

  const getReviewValue = (reviews: any[]) => {
    if (!reviews.length) {
      return '-';
    }
    const review = reviews[0];
    if (review?.review) {
      return review.review;
    }
    return '-';
  };

  const getCustomerReviewLabel = (record: Dispatch) => {
    const reviews = (record as any).reviews || [];
    const customerReviews = reviews.filter((review: any) => review.customer_id);
    return getReviewValue(customerReviews);
  };

  const getManagerReviewLabel = (record: Dispatch) => {
    const reviews = (record as any).reviews || [];
    const staffReviews = reviews.filter((review: any) => !review.customer_id);
    return getReviewValue(staffReviews);
  };

  const compareReferenceNumbers = (a?: string, b?: string) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  };

  const mapStatus = (status: string | undefined): 'Plan' | 'Collect' | 'Return' => {
    if (!status) return 'Plan';
    const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    if (normalized === 'Pending') {
      return 'Plan';
    }
    if (['Plan', 'Collect', 'Return'].includes(normalized)) {
      return normalized as 'Plan' | 'Collect' | 'Return';
    }
    return 'Plan';
  };
  const statusCounts = useMemo(() => {
    if (statusCountsFromApi) {
      return statusCountsFromApi;
    }
    const counts = { all: dispatches.length, Plan: 0, Collect: 0, Return: 0 };
    dispatches.forEach((dispatch) => {
      const mapped = mapStatus(dispatch.status);
      if (mapped === 'Plan') counts.Plan += 1;
      if (mapped === 'Collect') counts.Collect += 1;
      if (mapped === 'Return') counts.Return += 1;
    });
    return counts;
  }, [dispatches, mapStatus, statusCountsFromApi]);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Dispatches
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const getReferenceLabel = (record: Dispatch) => {
    if (record.type === 'business') {
      if (record.business?.name) return record.business.name;
      if (record.reference_id) return `Business #${record.reference_id}`;
      return '-';
    }
    if (record.type === 'personal') {
      if (record.customer?.name) return record.customer.name;
      if (record.reference_id) return `Customer #${record.reference_id}`;
      return '-';
    }
    return record.customer?.name || record.business?.name || '-';
  };

  const baseColumns = useMemo(
    () => [
    {
      title: 'Dispatch No.',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      sorter: (a: Dispatch, b: Dispatch) =>
        compareReferenceNumbers(
          formatDispatchNumber(a.id, a.reference_number),
          formatDispatchNumber(b.id, b.reference_number)
        ),
      render: (id: number, record: Dispatch) => formatDispatchNumber(id, record.reference_number),
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'name'],
      key: 'customer',
      width: 180,
      sorter: (a: Dispatch, b: Dispatch) => getReferenceLabel(a).localeCompare(getReferenceLabel(b)),
      render: (_: string, record: Dispatch) => (
        <Tooltip title={getReferenceLabel(record)} placement="topLeft">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getReferenceLabel(record)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Task',
      key: 'task',
      width: 180,
      sorter: (a: Dispatch, b: Dispatch) => formatTaskLabel(a).localeCompare(formatTaskLabel(b)),
      render: (_: unknown, record: Dispatch) => {
        const label = formatTaskLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Period',
      key: 'year_end',
      width: 120,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => compareYearMonth(a.year, b.year),
      render: (_: unknown, record: Dispatch) => (formatYearMonthValue(record.year) || '-'),
    },
    {
      title: 'Create',
      dataIndex: 'create_date',
      key: 'create_date',
      width: 120,
      sorter: (a: Dispatch, b: Dispatch) => {
        if (!a.create_date && !b.create_date) return 0;
        if (!a.create_date) return 1;
        if (!b.create_date) return -1;
        return new Date(a.create_date).getTime() - new Date(b.create_date).getTime();
      },
      render: (date: string) => formatDateValue(date),
    },
    {
      title: 'Collect',
      dataIndex: 'collect_date',
      key: 'collect_date',
      width: 120,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => {
        if (!a.collect_date && !b.collect_date) return 0;
        if (!a.collect_date) return 1;
        if (!b.collect_date) return -1;
        return new Date(a.collect_date).getTime() - new Date(b.collect_date).getTime();
      },
      render: (date: string) => formatDateValue(date),
    },
    {
      title: 'Collect Due',
      dataIndex: 'collect_due',
      key: 'collect_due',
      width: 130,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => {
        if (!a.collect_due && !b.collect_due) return 0;
        if (!a.collect_due) return 1;
        if (!b.collect_due) return -1;
        return new Date(a.collect_due).getTime() - new Date(b.collect_due).getTime();
      },
      render: (date: string) => formatDateValue(date),
    },
    {
      title: 'Return',
      dataIndex: 'return_date',
      key: 'return_date',
      width: 120,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => {
        if (!a.return_date && !b.return_date) return 0;
        if (!a.return_date) return 1;
        if (!b.return_date) return -1;
        return new Date(a.return_date).getTime() - new Date(b.return_date).getTime();
      },
      render: (date: string) => formatDateValue(date),
    },
    {
      title: 'Return Due',
      dataIndex: 'return_due',
      key: 'return_due',
      width: 130,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => {
        if (!a.return_due && !b.return_due) return 0;
        if (!a.return_due) return 1;
        if (!b.return_due) return -1;
        return new Date(a.return_due).getTime() - new Date(b.return_due).getTime();
      },
      render: (date: string) => formatDateValue(date),
    },
    {
      title: 'Check-In Checklist',
      key: 'checkin_checklist',
      width: 160,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) =>
        getChecklistLabel(a.checkins).localeCompare(getChecklistLabel(b.checkins)),
      render: (_: unknown, record: Dispatch) => {
        const label = getChecklistLabel(record.checkins);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Check-Out Checklist',
      key: 'checkout_checklist',
      width: 170,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) =>
        getChecklistLabel(a.checkouts).localeCompare(getChecklistLabel(b.checkouts)),
      render: (_: unknown, record: Dispatch) => {
        const label = getChecklistLabel(record.checkouts);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Check-In Photo',
      key: 'checkin_photo',
      width: 140,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) =>
        getPhotoCountLabel(a.collects).localeCompare(getPhotoCountLabel(b.collects)),
      render: (_: unknown, record: Dispatch) => {
        const label = getPhotoCountLabel(record.collects);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Check-Out Photo',
      key: 'checkout_photo',
      width: 150,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) =>
        getPhotoCountLabel(a.returns).localeCompare(getPhotoCountLabel(b.returns)),
      render: (_: unknown, record: Dispatch) => {
        const label = getPhotoCountLabel(record.returns);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Customer Review',
      key: 'customer_review',
      width: 170,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => getCustomerReviewLabel(a).localeCompare(getCustomerReviewLabel(b)),
      render: (_: unknown, record: Dispatch) => {
        const label = getCustomerReviewLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Manager Review',
      key: 'manager_review',
      width: 170,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => getManagerReviewLabel(a).localeCompare(getManagerReviewLabel(b)),
      render: (_: unknown, record: Dispatch) => {
        const label = getManagerReviewLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Task Type',
      dataIndex: ['taskType', 'name'],
      key: 'taskType',
      width: 150,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => (a.taskType?.name || '').localeCompare(b.taskType?.name || ''),
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 100,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => compareYearMonth(a.year, b.year),
      render: (val: string | number | undefined) => (formatYearMonthValue(val) || '-'),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 180,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => (a.location || '').localeCompare(b.location || ''),
    },
    {
      title: 'Agent',
      dataIndex: ['agent', 'name'],
      key: 'agent',
      width: 160,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => (a.agent?.name || '').localeCompare(b.agent?.name || ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      visible: false,
      sorter: (a: Dispatch, b: Dispatch) => (a.status || 'Plan').localeCompare(b.status || 'Plan'),
      render: (status: string) => {
        const mappedStatus = mapStatus(status);
        const statusConfig = {
          Plan: { tagBgColor: '#E6F7FF', tagBorderColor: '#91D5FF', tagTextColor: '#1890ff', label: 'Plan' },
          Collect: { tagBgColor: '#F6FFED', tagBorderColor: '#73D13D', tagTextColor: '#52C41A', label: 'Collect' },
          Return: { tagBgColor: '#FFF7E6', tagBorderColor: '#FFA940', tagTextColor: '#FF8C00', label: 'Return' },
        };
        const config = statusConfig[mappedStatus];
        return (
          <Tag 
            color={config.tagTextColor} 
            style={{ 
              backgroundColor: config.tagBgColor, 
              borderColor: config.tagBorderColor, 
              color: config.tagTextColor 
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    ],
    []
  );

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
    setColumnConfig,
  } = useColumnVisibility(baseColumns);
  const maxVisibleColumns = 10;
  const { persistVisibleKeys } = useListingColumnsPersistence(
    listingKey,
    baseColumns,
    maxVisibleColumns,
    setColumnConfig
  );
  const { persistColumnWidths } = useListingColumnWidthsPersistence(
    listingKey,
    baseColumns,
    setColumnWidthByKey
  );
  const { persistSort } = useListingSortPersistence(
    listingKey,
    baseColumns,
    setSortKey,
    setSortOrder
  );
  const columnGroups = useMemo(
    () => [
      {
        title: 'Details',
        keys: [
          'id',
          'customer',
          'task',
          'year_end',
          'create_date',
          'collect_date',
          'collect_due',
          'return_date',
          'return_due',
        ],
      },
      {
        title: 'Checklist',
        keys: ['checkin_checklist', 'checkout_checklist'],
      },
      {
        title: 'Photo',
        keys: ['checkin_photo', 'checkout_photo'],
      },
      {
        title: 'Review',
        keys: ['customer_review', 'manager_review'],
      },
    ],
    []
  );
  const defaultVisibleKeys = useMemo(
    () => baseColumns.filter((col) => !('visible' in col) || (col as { visible?: boolean }).visible !== false).map((col) => col.key),
    [baseColumns]
  );
  const handleColumnVisibilityChangeLimited = useCallback(
    (keys: React.Key[]) => {
      if (keys.length > maxVisibleColumns) {
        message.warning(`You can select up to ${maxVisibleColumns} columns.`);
        return;
      }
      handleColumnVisibilityChange(keys);
      persistVisibleKeys(keys);
    },
    [handleColumnVisibilityChange, maxVisibleColumns, persistVisibleKeys]
  );
  const columnSelectionLocked = pendingVisibleKeys.length >= maxVisibleColumns;
  const filteredColumnConfig = useMemo(() => {
    if (!columnsSearch.trim()) {
      return columnConfig;
    }
    const query = columnsSearch.toLowerCase();
    return columnConfig.filter((col) => col.title.toLowerCase().includes(query));
  }, [columnConfig, columnsSearch]);
  const columnConfigByKey = useMemo(
    () => new Map(columnConfig.map((col) => [col.key, col])),
    [columnConfig]
  );

  const getSearchValueForColumn = useCallback((col: any) => {
    if (col.dataIndex === 'id') return (record: Dispatch) => formatDispatchNumber(record.id, record.reference_number);
    if (Array.isArray(col.dataIndex) && col.dataIndex[0] === 'customer' && col.dataIndex[1] === 'name') {
      return (record: Dispatch) => getReferenceLabel(record);
    }
    if (Array.isArray(col.dataIndex) && col.dataIndex[0] === 'taskType' && col.dataIndex[1] === 'name') {
      return (record: Dispatch) => record.taskType?.name;
    }
    if (col.dataIndex === 'year') return (record: Dispatch) => formatYearMonthValue(record.year);
    if (col.dataIndex === 'location') return (record: Dispatch) => record.location;
    if (Array.isArray(col.dataIndex) && col.dataIndex[0] === 'agent' && col.dataIndex[1] === 'name') {
      return (record: Dispatch) => record.agent?.name;
    }
    if (col.dataIndex === 'create_date') return (record: Dispatch) => formatDateValue(record.create_date);
    if (col.key === 'task') return (record: Dispatch) => formatTaskLabel(record);
    if (col.key === 'year_end') return (record: Dispatch) => formatYearMonthValue(record.year);
    if (col.key === 'collect_date') return (record: Dispatch) => formatDateValue(record.collect_date);
    if (col.key === 'collect_due') return (record: Dispatch) => formatDateValue(record.collect_due);
    if (col.key === 'return_date') return (record: Dispatch) => formatDateValue(record.return_date);
    if (col.key === 'return_due') return (record: Dispatch) => formatDateValue(record.return_due);
    if (col.key === 'checkin_checklist') return (record: Dispatch) => getChecklistLabel(record.checkins);
    if (col.key === 'checkout_checklist') return (record: Dispatch) => getChecklistLabel(record.checkouts);
    if (col.key === 'checkin_photo') return (record: Dispatch) => getPhotoCountLabel(record.collects);
    if (col.key === 'checkout_photo') return (record: Dispatch) => getPhotoCountLabel(record.returns);
    if (col.key === 'customer_review') return (record: Dispatch) => getCustomerReviewLabel(record);
    if (col.key === 'manager_review') return (record: Dispatch) => getManagerReviewLabel(record);
    if (col.dataIndex === 'status') return (record: Dispatch) => mapStatus(record.status);
    const key = String(col.key || '').toLowerCase();
    if (typeof col.dataIndex === 'string') {
      const dataIndex = col.dataIndex.toLowerCase();
      if (dataIndex.includes('date') || dataIndex.includes('due')) {
        return (record: Dispatch) => formatDateValue((record as any)[col.dataIndex]);
      }
    }
    if (key.includes('date') || key.includes('due')) {
      return (record: Dispatch) => formatDateValue((record as any)[col.key]);
    }
    return undefined as unknown as (record: Dispatch) => string | number | null | undefined;
  }, []);

  const searchColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const filteredDispatches = useMemo(() => {
    if (filter === 'all') {
      return dispatches;
    }
    return dispatches.filter((dispatch) => mapStatus(dispatch.status) === filter);
  }, [dispatches, filter]);

  const { columnsWithSearch, filteredData, searchRowCells } = useTableColumnSearch<Dispatch>(
    filteredDispatches,
    memoizedColumns as any,
    searchColumns as any,
    { splitHeader: true, listingKey }
  );

  const tableColumns = columnsWithSearch as any[];
  const totalValue = typeof totalCount === 'number' ? totalCount : filteredData.length;
  const totalLabel = `Total: ${totalValue} ${totalValue === 1 ? 'dispatch' : 'dispatches'}`;
  const totalColumnCount = tableColumns.length + 1;
  const minColumnWidth = 50;
  const baseColumnWidths = useMemo(
    () =>
      tableColumns.map((col) => {
        const key = String(col.key);
        const cached = columnWidthByKey[key];
        if (typeof cached === 'number') {
          return cached;
        }
        if (typeof col.width === 'number') {
          return col.width;
        }
        if (typeof col.width === 'string') {
          const parsed = Number.parseFloat(col.width);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return 150;
      }),
    [tableColumns, columnWidthByKey]
  );
  const columnWidths = useMemo(() => {
    if (availableWidth == null) {
      return baseColumnWidths;
    }
    const total = baseColumnWidths.reduce((sum, width) => sum + width, 0) || 1;
    if (total <= availableWidth) {
      return baseColumnWidths;
    }
    const scale = availableWidth / total;
    return baseColumnWidths.map((width) => Math.max(minColumnWidth, Math.floor(width * scale)));
  }, [baseColumnWidths, availableWidth]);
  const handleResize = useCallback(
    (targetIndex: number) => (_e: unknown, data: { size: { width: number } }) => {
      const keys = tableColumns.map((col) => String(col.key));
      const current = columnWidths.map((width) => Math.max(minColumnWidth, Math.round(width)));
      const totalAvailable = availableWidth ?? current.reduce((sum, width) => sum + width, 0);
      const rightIndexes = current.map((_width, index) => index).filter((index) => index > targetIndex);
      if (rightIndexes.length === 0) {
        return;
      }
      const rightReducible = rightIndexes.reduce(
        (sum, index) => sum + Math.max(0, current[index] - minColumnWidth),
        0
      );
      const maxTarget = Math.max(minColumnWidth, current[targetIndex] + rightReducible);
      const nextWidth = Math.min(Math.max(Math.round(data.size.width), minColumnWidth), maxTarget);
      const delta = nextWidth - current[targetIndex];
      if (delta === 0) {
        return;
      }

      const updated = [...current];
      updated[targetIndex] = nextWidth;

      if (delta > 0) {
        const reducible = rightIndexes.map((index) => Math.max(0, current[index] - minColumnWidth));
        let remaining = delta;
        const totalReducible = reducible.reduce((sum, value) => sum + value, 0);
        if (totalReducible === 0) {
          return;
        }
        rightIndexes.forEach((index, position) => {
          const room = reducible[position];
          if (room === 0) return;
          const take = Math.min(room, Math.round((delta * room) / totalReducible));
          updated[index] = current[index] - take;
          remaining -= take;
        });
        if (remaining > 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining > 0; idx += 1) {
            const index = rightIndexes[idx];
            if (updated[index] > minColumnWidth) {
              updated[index] -= 1;
              remaining -= 1;
            }
          }
        }
      } else {
        const add = -delta;
        const totalRight = rightIndexes.reduce((sum, index) => sum + current[index], 0);
        let remaining = add;
        rightIndexes.forEach((index) => {
          const give = Math.round((add * current[index]) / (totalRight || 1));
          updated[index] = current[index] + give;
          remaining -= give;
        });
        if (remaining !== 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining !== 0; idx += 1) {
            const index = rightIndexes[idx];
            updated[index] += remaining > 0 ? 1 : -1;
            remaining += remaining > 0 ? -1 : 1;
          }
        }
      }

      const nextWidths: Record<string, number> = {};
      keys.forEach((key, index) => {
        nextWidths[key] = updated[index];
      });
      setColumnWidthByKey((prev) => {
        const merged = { ...prev, ...nextWidths };
        persistColumnWidths(merged);
        return merged;
      });
    },
    [availableWidth, columnWidths, minColumnWidth, persistColumnWidths, tableColumns]
  );

  useEffect(() => {
    const updateAvailableWidth = () => {
      const containerWidth = tableScrollRef.current?.clientWidth ?? 0;
      const borderAdjust = Math.max(totalColumnCount - 1, 0);
      const nextAvailable = Math.max(containerWidth - 48 - borderAdjust, 0);
      setAvailableWidth(nextAvailable || null);
    };

    updateAvailableWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateAvailableWidth);
      return () => window.removeEventListener('resize', updateAvailableWidth);
    }
    const observer = new ResizeObserver(updateAvailableWidth);
    if (tableScrollRef.current) {
      observer.observe(tableScrollRef.current);
    }
    return () => observer.disconnect();
  }, [totalColumnCount]);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) {
      return;
    }
    const updateHeight = () => {
      const rect = container.getBoundingClientRect();
      const nextHeight = Math.max(window.innerHeight - rect.top - 24, 200);
      setViewportHeight(nextHeight);
      const nextScrollbarWidth = Math.max(container.offsetWidth - container.clientWidth, 0);
      setScrollbarWidth(nextScrollbarWidth);
    };
    updateHeight();
    const mutationObserver = new MutationObserver(() => {
      updateHeight();
    });
    mutationObserver.observe(container, { childList: true, subtree: true, attributes: true });
    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateHeight);
    return () => {
      mutationObserver.disconnect();
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeight);
    };
  }, [filteredData.length]);


  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) {
      return filteredData;
    }
    const column = tableColumns.find((col: any) => String(col.key) === sortKey);
    const sorter = column?.sorter;
    if (typeof sorter !== 'function') {
      return filteredData;
    }
    const sorted = [...filteredData].sort(sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [filteredData, tableColumns, sortKey, sortOrder]);

  const rowHeight = 44;
  const { focusedRowId, handleTableKeyDown, handleRowClick } = useListingTableFocus<Dispatch>({
    rows: sortedData,
    getRowId: (row) => row.id,
    containerRef: tableScrollRef,
    rowHeight,
  });

  const overscan = 6;
  const totalRows = sortedData.length;
  const visibleRange = useMemo(() => {
    if (!viewportHeight || totalRows === 0) {
      return { start: 0, end: totalRows, topPad: 0, bottomPad: 0 };
    }
    const maxStart = Math.max(0, totalRows - 1);
    const rawStart = Math.min(
      maxStart,
      Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    );
    const rawEnd = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
    );
    const start = Math.min(rawStart, totalRows);
    const end = Math.max(start, rawEnd);
    const topPad = start * rowHeight;
    const bottomPad = Math.max(0, (totalRows - end) * rowHeight);
    return { start, end, topPad, bottomPad };
  }, [overscan, rowHeight, scrollTop, totalRows, viewportHeight]);
  const visibleRows = useMemo(
    () => sortedData.slice(visibleRange.start, visibleRange.end),
    [sortedData, visibleRange]
  );

  const handleSort = (key: string, sorter?: unknown) => {
    if (typeof sorter !== 'function') {
      return;
    }
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder('ascend');
      persistSort(key, 'ascend');
      return;
    }
    if (sortOrder === 'ascend') {
      setSortOrder('descend');
      persistSort(key, 'descend');
      return;
    }
    setSortKey(null);
    setSortOrder(null);
    persistSort(null, null);
  };

  const getCellValue = (record: Dispatch, col: any) => {
    if (!col.dataIndex) {
      return undefined;
    }
    if (Array.isArray(col.dataIndex)) {
      return col.dataIndex.reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), record);
    }
    return (record as any)[col.dataIndex];
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space size={4} wrap>
          <Button
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            type={filter === 'Plan' ? 'primary' : 'default'}
            onClick={() => setFilter('Plan')}
          >
            Plan ({statusCounts.Plan})
          </Button>
          <Button
            type={filter === 'Collect' ? 'primary' : 'default'}
            onClick={() => setFilter('Collect')}
          >
            Collect ({statusCounts.Collect})
          </Button>
          <Button
            type={filter === 'Return' ? 'primary' : 'default'}
            onClick={() => setFilter('Return')}
          >
            Return ({statusCounts.Return})
          </Button>
        </Space>
        <Space>
          <Button
            icon={<ColumnHeightOutlined />}
            onClick={() => {
              setPendingVisibleKeys(visibleKeys);
              setColumnsSearch('');
              setColumnsModalOpen(true);
            }}
          >
            Columns
          </Button>
          {canDelete && (selectAll || selectedRowKeys.length > 0) && (
            <Popconfirm
              title={selectAll ? 'Delete all dispatches?' : `Delete ${selectedRowKeys.length} dispatch${selectedRowKeys.length === 1 ? '' : 'es'}?`}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleBatchDelete}
            >
              <Button danger loading={deleting}>
                Batch Delete
              </Button>
            </Popconfirm>
          )}
          {(selectAll || selectedRowKeys.length > 0) && (
            <Button
              icon={<span style={{ color: '#9254de' }}><DownloadOutlined /></span>}
              onClick={handleBatchExport}
              style={{
                borderColor: '#9254de',
                color: '#9254de',
              }}
            >
              Batch Export
            </Button>
          )}
          {canCreate && (
            <Button icon={<UploadOutlined />} onClick={handleImportNavigate}>
              Import
            </Button>
          )}
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/dispatches/new')}
            >
              New Dispatch
            </Button>
          )}
        </Space>
      </div>
      <Modal
        title="Select Columns"
        open={columnsModalOpen}
        onCancel={() => setColumnsModalOpen(false)}
        width={820}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="link"
              onClick={() => {
                setPendingVisibleKeys(defaultVisibleKeys);
              }}
            >
              Reset to default
            </Button>
            <Space>
              <Button onClick={() => setColumnsModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => {
                  handleColumnVisibilityChangeLimited(pendingVisibleKeys);
                  setColumnsModalOpen(false);
                }}
              >
                Apply
              </Button>
            </Space>
          </div>
        )}
      >
        <Input
          placeholder="Search Columns"
          prefix={<SearchOutlined />}
          value={columnsSearch}
          onChange={(event) => setColumnsSearch(event.target.value)}
          style={{ marginBottom: 16 }}
        />
        {columnGroups.map((group) => {
          const groupColumns = group.keys
            .map((key) => columnConfigByKey.get(key))
            .filter((col): col is typeof columnConfig[number] => Boolean(col))
            .filter((col) => filteredColumnConfig.some((filtered) => filtered.key === col.key));

          if (groupColumns.length === 0) {
            return null;
          }

          return (
            <div key={group.title} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                {group.title.toUpperCase()}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                {groupColumns.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AntCheckbox
                      checked={pendingVisibleKeys.includes(col.key)}
                      disabled={columnSelectionLocked && !pendingVisibleKeys.includes(col.key)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setPendingVisibleKeys((prev) => [...prev, col.key]);
                        } else {
                          setPendingVisibleKeys((prev) => prev.filter((key) => key !== col.key));
                        }
                      }}
                    />
                    <span style={{ color: columnSelectionLocked && !pendingVisibleKeys.includes(col.key) ? '#9ca3af' : undefined }}>
                      {col.title}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </Modal>
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}
      <div className="flowbite-listing-table-shell">
        <div style={{ paddingRight: scrollbarWidth }}>
          <FlowbiteTable className="flowbite-listing-table header-only w-full table-fixed" hoverable>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`head_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableHeadCell className="w-12">
                  <Checkbox
                    checked={selectAll || (sortedData.length > 0 && selectedRowKeys.length === sortedData.length)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectAll(true);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      } else {
                        setSelectAll(false);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      }
                    }}
                  />
                </TableHeadCell>
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  const isSortable = typeof col.sorter === 'function';
                  const isSorted = sortKey === key;
                  const sortIndicator =
                    isSorted && sortOrder
                      ? sortOrder === 'ascend'
                        ? '▲'
                        : '▼'
                      : '';
                  const isFirstDataColumn = index === 0;
                  const maxWidth =
                    availableWidth != null
                      ? Math.max(minColumnWidth, availableWidth - minColumnWidth * (columnWidths.length - 1))
                      : undefined;

                  return (
                    <FlowbiteResizableHeader
                      key={key}
                      width={columnWidths[index]}
                      maxWidth={maxWidth}
                      onResize={handleResize(index)}
                      onClick={() => handleSort(key, col.sorter)}
                      className={isSortable ? 'cursor-pointer select-none' : undefined}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div>{col.title}</div>
                          {isFirstDataColumn && (
                            <div className="mt-1 text-xs text-gray-400">{totalLabel}</div>
                          )}
                        </div>
                        {isSortable && (
                          <span className="text-xs text-gray-400">{sortIndicator}</span>
                        )}
                      </div>
                    </FlowbiteResizableHeader>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableHeadCell className="w-12" />
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  return (
                    <TableHeadCell key={`${key}-search`} className="py-2">
                      {searchRowCells?.[index] ?? null}
                    </TableHeadCell>
                  );
                })}
              </TableRow>
            </TableHead>
          </FlowbiteTable>
        </div>
        <div
          className="flowbite-listing-table-scroll"
          ref={tableScrollRef}
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          style={
            viewportHeight
              ? {
                  height: viewportHeight,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }
              : { overflowY: 'auto', overflowX: 'hidden' }
          }
        >
          <FlowbiteTable className="flowbite-listing-table w-full table-fixed" hoverable>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`body_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableBody className="divide-y">
              {loading && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    No dispatches
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length > 0 && (
                <>
                  {visibleRange.topPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.topPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                  {visibleRows.map((record: Dispatch, index: number) => {
                    const isSelected = selectedRowKeys.includes(record.id);
                    const isFocused = focusedRowId === record.id;
                    return (
                      <TableRow
                        key={record.id}
                        data-row-id={record.id}
                        className={`hover:bg-gray-50 ${isFocused ? 'bg-blue-50' : ''}`}
                        onDoubleClick={canView ? () => navigate(`/dispatches/${record.id}/edit`) : undefined}
                        onClick={() => handleRowClick(record.id)}
                      >
                        <TableCell className="w-12">
                          <Checkbox
                            checked={selectAll ? !excludedRowKeys.includes(record.id) : isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => {
                              if (selectAll) {
                                setExcludedRowKeys((prev) =>
                                  prev.includes(record.id)
                                    ? prev.filter((key) => key !== record.id)
                                    : [...prev, record.id]
                                );
                                return;
                              }
                              setSelectedRowKeys((prev) =>
                                prev.includes(record.id)
                                  ? prev.filter((key) => key !== record.id)
                                  : [...prev, record.id]
                              );
                            }}
                          />
                        </TableCell>
                        {tableColumns.map((col, index) => {
                          const value = getCellValue(record, col);
                          const content =
                            typeof col.render === 'function'
                              ? col.render(value, record, index)
                              : value ?? '-';
                          return (
                            <TableCell key={`${record.id}_${String(col.key)}`} style={{ width: columnWidths[index] }}>
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {visibleRange.bottomPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.bottomPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </FlowbiteTable>
        </div>
      </div>
    </div>
  );
};
